import React, { useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import useEditorStore from '../store/useEditorStore';
import { sharedCameraQuat } from '../shared/cameraSync';

const h = React.createElement;

function makeFaceTexture(label, accent = '#ec1d8c') {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 256;
  const ctx = c.getContext('2d');
  // Subtle dark gradient
  const grad = ctx.createLinearGradient(0, 0, 256, 256);
  grad.addColorStop(0, '#26262e');
  grad.addColorStop(0.5, '#1a1a20');
  grad.addColorStop(1, '#0e0e14');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);
  // Inner glow on edges
  const radial = ctx.createRadialGradient(128, 128, 60, 128, 128, 180);
  radial.addColorStop(0, 'rgba(236,29,140,0.04)');
  radial.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = radial;
  ctx.fillRect(0, 0, 256, 256);
  // Accent border (thin, modern)
  ctx.strokeStyle = accent;
  ctx.lineWidth = 4;
  ctx.strokeRect(8, 8, 240, 240);
  // Soft inner stroke
  ctx.strokeStyle = 'rgba(255,255,255,0.05)';
  ctx.lineWidth = 1;
  ctx.strokeRect(14, 14, 228, 228);
  // Label
  ctx.fillStyle = '#ffffff';
  ctx.font = '700 44px Inter, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(236,29,140,0.4)';
  ctx.shadowBlur = 8;
  ctx.fillText(label, 128, 128);
  ctx.shadowBlur = 0;
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 8;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
}

const FACE_LABELS = ['DROITE', 'GAUCHE', 'HAUT', 'BAS', 'AVANT', 'ARRIÈRE'];
const FACE_KEYS  = ['right',  'left',   'top',  'bottom', 'front', 'back'];

// Build a rounded box geometry (dice-like) by deforming a subdivided box
function makeRoundedBoxGeometry(size, radius, segs) {
  const half = size / 2;
  const inner = half - radius;
  const geo = new THREE.BoxGeometry(size, size, size, segs, segs, segs);
  const pos = geo.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const cx = Math.max(-inner, Math.min(inner, v.x));
    const cy = Math.max(-inner, Math.min(inner, v.y));
    const cz = Math.max(-inner, Math.min(inner, v.z));
    const dx = v.x - cx, dy = v.y - cy, dz = v.z - cz;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len > 0.0001) {
      const k = radius / len;
      pos.setXYZ(i, cx + dx * k, cy + dy * k, cz + dz * k);
    }
  }
  geo.computeVertexNormals();
  return geo;
}

function CubeWidget() {
  const setOrientation = useEditorStore((s) => s.setOrientation);

  const cube = useMemo(() => {
    const materials = FACE_LABELS.map((lbl) => {
      const t = makeFaceTexture(lbl);
      return new THREE.MeshPhysicalMaterial({
        map: t,
        roughness: 0.25,
        metalness: 0.55,
        clearcoat: 1.0,
        clearcoatRoughness: 0.15,
        reflectivity: 0.6,
        envMapIntensity: 1.2,
      });
    });
    const geo = makeRoundedBoxGeometry(1.2, 0.16, 6);
    const m = new THREE.Mesh(geo, materials);
    return m;
  }, []);

  const tmpQ = useMemo(() => new THREE.Quaternion(), []);
  useFrame(() => {
    if (!cube) return;
    tmpQ.copy(sharedCameraQuat).invert();
    cube.quaternion.slerp(tmpQ, 0.18);
  });

  const onCubeClick = (e) => {
    e.stopPropagation();
    if (!e.face) return;
    const idx = e.face.materialIndex;
    const key = FACE_KEYS[idx];
    if (key) setOrientation(key);
  };

  return h('primitive', { object: cube, onClick: onCubeClick });
}

function CubeScene() {
  return h(React.Fragment, null,
    h('ambientLight', { intensity: 0.6 }),
    h('directionalLight', { position: [4, 5, 6], intensity: 1.1, color: '#ffffff' }),
    h('directionalLight', { position: [-4, -2, -4], intensity: 0.5, color: '#ffcce0' }),
    h('pointLight', { position: [0, 0, 5], intensity: 0.6, color: '#ec1d8c' }),
    h(CubeWidget, null),
  );
}

export default function OrientationCube() {
  return h(Canvas, {
    camera: { position: [0, 0, 4.2], fov: 28 },
    style: { background: 'transparent', cursor: 'pointer' },
    gl: { antialias: true, alpha: true },
  }, h(CubeScene, null));
}
